import type { Dayjs } from 'dayjs';
import type { DateRange } from 'src/components/range-availability-calendar';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Stack } from '@mui/material';

import { ResponsiveModal } from 'src/components/ResponsiveModal';
import {
  ActivityDateRangePicker,
  getActivityDateRangePickerBounds,
  isValidMobiscrollDateRange,
  mobiscrollRangeToDayjs,
} from 'src/components/activity-date-range-picker';

import type { ActivityDatePreferenceType, ActivityDatePreference } from './activity-date-preference.types';

type AddPreferenceType = Extract<ActivityDatePreferenceType, 'preferred' | 'excluded'>;

const ADD_PREFERENCE_MODAL_MIN_WIDTH = 784;

interface ActivityAddDatePreferenceDialogProps {
  open: boolean;
  completionWindow?: DateRange;
  editingPreference?: ActivityDatePreference | null;
  onClose: () => void;
  onApply: (payload: { type: AddPreferenceType; start: Dayjs; end: Dayjs }) => void | Promise<void>;
  isSaving?: boolean;
  hideTypeSelector?: boolean;
  fixedPreferenceType?: AddPreferenceType;
  /** When true, the user may pick any date (no completion-window clamp). */
  unrestrictedDates?: boolean;
}

export const ActivityAddDatePreferenceDialog = memo(
  ({
    open,
    completionWindow,
    editingPreference = null,
    onClose,
    onApply,
    isSaving = false,
    hideTypeSelector = false,
    fixedPreferenceType = 'preferred',
    unrestrictedDates = false,
  }: ActivityAddDatePreferenceDialogProps) => {
    const { t } = useTranslation('activities');
    const [preferenceType, setPreferenceType] = useState<AddPreferenceType>(fixedPreferenceType);
    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);

    useEffect(() => {
      if (!open) {
        return;
      }

      if (editingPreference && editingPreference.type !== 'completionWindow') {
        setPreferenceType(editingPreference.type);
        setDateRange([
          editingPreference.start.startOf('day').toDate(),
          editingPreference.end.startOf('day').toDate(),
        ]);
        return;
      }

      setPreferenceType(hideTypeSelector ? fixedPreferenceType : 'preferred');
      setDateRange(null);
    }, [editingPreference, fixedPreferenceType, hideTypeSelector, open]);

    const handleApply = useCallback(async () => {
      if (!dateRange) {
        return;
      }

      const { start, end } = mobiscrollRangeToDayjs(dateRange);
      await onApply({
        type: preferenceType,
        start,
        end,
      });
    }, [dateRange, onApply, preferenceType]);

    const pickerInstanceKey = editingPreference
      ? `edit-${String(editingPreference.id)}`
      : 'add';

    const pickerBounds = useMemo(
      () => (unrestrictedDates ? { hasSelectableRange: true } : getActivityDateRangePickerBounds(completionWindow!)),
      [completionWindow, unrestrictedDates]
    );

    return (
      <ResponsiveModal
        open={open}
        onClose={onClose}
        minWidth={ADD_PREFERENCE_MODAL_MIN_WIDTH}
        maxWidth={`min(${ADD_PREFERENCE_MODAL_MIN_WIDTH}px, calc(100vw - 32px))`}
        showCloseButton={false}
        padding={2}
      >
        <Stack spacing={0} sx={{ width: '100%' }}>
          {!hideTypeSelector && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Stack direction="row" spacing={0}>
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={() => setPreferenceType('preferred')}
                  sx={{
                    minWidth: 64,
                    height: 36,
                    px: 1.5,
                    borderRadius: '8px 0 0 8px',
                    fontWeight: 700,
                    boxShadow: 'none',
                    bgcolor:
                      preferenceType === 'preferred' ? 'grey.900' : 'action.disabledBackground',
                    color: preferenceType === 'preferred' ? 'common.white' : 'text.primary',
                    '&:hover': {
                      boxShadow: 'none',
                      bgcolor:
                        preferenceType === 'preferred' ? 'grey.800' : 'action.disabledBackground',
                    },
                  }}
                >
                  {t('activityModal.dates.addPreferenceDialog.preferredDates')}
                </Button>
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={() => setPreferenceType('excluded')}
                  sx={{
                    minWidth: 64,
                    height: 36,
                    px: 1.5,
                    borderRadius: '0 8px 8px 0',
                    fontWeight: 700,
                    boxShadow: 'none',
                    bgcolor:
                      preferenceType === 'excluded' ? 'grey.900' : 'action.disabledBackground',
                    color: preferenceType === 'excluded' ? 'common.white' : 'text.primary',
                    '&:hover': {
                      boxShadow: 'none',
                      bgcolor:
                        preferenceType === 'excluded' ? 'grey.800' : 'action.disabledBackground',
                    },
                  }}
                >
                  {t('activityModal.dates.addPreferenceDialog.excludedDates')}
                </Button>
              </Stack>
            </Box>
          )}

          <ActivityDateRangePicker
            completionWindow={completionWindow}
            unrestricted={unrestrictedDates}
            value={dateRange}
            onChange={setDateRange}
            instanceKey={pickerInstanceKey}
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2, py: 2 }}>
            <Button variant="outlined" color="inherit" onClick={onClose} disabled={isSaving}>
              {t('activityModal.dates.addPreferenceDialog.cancel')}
            </Button>
            <Button
              variant="contained"
              color="inherit"
              disabled={!pickerBounds.hasSelectableRange || !isValidMobiscrollDateRange(dateRange) || isSaving}
              onClick={handleApply}
            >
              {t('activityModal.dates.addPreferenceDialog.apply')}
            </Button>
          </Stack>
        </Stack>
      </ResponsiveModal>
    );
  }
);

ActivityAddDatePreferenceDialog.displayName = 'ActivityAddDatePreferenceDialog';
